import React, { useEffect, useState } from 'react';
import { Loader2, FileWarning, Paperclip, X } from 'lucide-react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './dialog';
import { api } from '../../services/api';

/**
 * Aperçu d'un justificatif de caisse.
 *
 * POURQUOI UN APERÇU ET PAS UN LIEN
 * ---------------------------------
 * La pièce est désormais servie par une route authentifiée : le jeton voyage
 * dans l'en-tête, qu'un `<a href>` ne sait pas porter. Elle arrive donc en
 * blob. Ouvrir ce blob dans un onglet supposerait un `window.open` APRÈS un
 * `await` — que les bloqueurs de fenêtres interceptent. L'aperçu est fiable ;
 * la règle du drill-in ne s'y oppose pas, un document n'étant pas une fiche.
 *
 * L'URL d'objet est révoquée à la fermeture : sans cela le blob reste en
 * mémoire tant que l'onglet vit.
 */
const VisionneuseJustificatif = ({
  ouvert,
  onFermer,
  source,
  id,
  piece = 'depense',
  titre = 'Justificatif',
  sousTitre = ''
}) => {
  const [chargement, setChargement] = useState(false);
  const [url, setUrl] = useState(null);
  const [typeMime, setTypeMime] = useState('');
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!ouvert || !source || !id) return;

    let annule = false;
    let urlCreee = null;

    const charger = async () => {
      setChargement(true);
      setErreur(null);
      setUrl(null);

      const reponse = await api.portefeuille.getJustificatif(source, id, piece);

      // Le dialogue a pu se fermer pendant la requête : afficher alors le
      // document reviendrait à rouvrir ce que l'utilisateur vient de quitter.
      if (annule) {
        if (reponse.success) URL.revokeObjectURL(reponse.url);
        return;
      }

      if (reponse.success) {
        urlCreee = reponse.url;
        setUrl(reponse.url);
        setTypeMime(reponse.blob?.type || '');
      } else {
        setErreur(reponse.message || 'Justificatif introuvable.');
      }
      setChargement(false);
    };

    charger();

    return () => {
      annule = true;
      if (urlCreee) URL.revokeObjectURL(urlCreee);
    };
  }, [ouvert, source, id, piece]);

  const estImage = typeMime.startsWith('image/');

  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer?.()}>
      <DialogContent
        // Hauteur DÉFINIE, et non un simple `max-h` : un `height: 100%` ne se
        // résout que contre un parent dont la hauteur est connue. Avec un
        // `min-height` seul, l'iframe retombait sur ses 150 px par défaut —
        // d'où le document réduit à un bandeau et le grand vide au-dessous.
        className="flex h-[92vh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0"
        // On lit un document : un clic à côté ne doit pas le faire disparaître.
        // Échap reste actif — c'est un geste délibéré, pas un faux mouvement.
        onInteractOutside={(evenement) => evenement.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Paperclip size={18} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="truncate text-lg font-semibold text-gray-900">
              {titre}
            </DialogTitle>
            {sousTitre && (
              <DialogDescription className="truncate text-sm text-gray-600">
                {sousTitre}
              </DialogDescription>
            )}
          </div>

          {/* Fermeture explicite : sans elle, le clic extérieur étant bloqué,
              il ne resterait que la touche Échap. */}
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </DialogHeader>

        {/* Le document occupe la hauteur disponible : des hauteurs fixes se
            battraient avec le `flex` du dialogue. */}
        {/* `min-h-0` : sans lui, un enfant de conteneur flex refuse de
            descendre sous sa taille de contenu et déborde du dialogue. */}
        <div className="flex min-h-0 flex-1 overflow-hidden bg-gray-50">
          {chargement && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-500">
              <Loader2 size={24} className="animate-spin text-[#246990]" />
              <p className="text-sm">Ouverture du justificatif…</p>
            </div>
          )}

          {!chargement && erreur && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
              <FileWarning size={30} className="text-gray-300" />
              <p className="text-sm font-semibold text-gray-600">{erreur}</p>
              <p className="max-w-md text-[11px] leading-snug text-gray-500">
                La pièce n'a pas été jointe, ou le fichier n'est plus sur le serveur.
              </p>
            </div>
          )}

          {!chargement && !erreur && url && (
            estImage ? (
              <div className="flex flex-1 items-center justify-center overflow-auto p-3">
                <img src={url} alt={titre} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <iframe src={url} title={titre} className="h-full w-full flex-1 border-0" />
            )
          )}
        </div>

        <div className="flex justify-end border-t bg-gray-50 px-5 py-4">
          <Button
            variant="outline"
            className="h-9 min-w-[168px] justify-center gap-2"
            onClick={onFermer}
          >
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisionneuseJustificatif;
